import React from "react";
import { useCallback, useContext, useEffect, useState, useMemo } from "react";

// packages
import { useTranslation } from "react-i18next";
import classNames from "classnames";

// components
import PrimaryButton, {
  PrimaryButtonType,
} from "../../../components/common/PrimaryButton";
import Modal from "@mui/material/Modal";
import StatusTimeline from "../contractDetails/StatusTimeline";
import Status from "../contractDetails/status/Status";
import BrokerPaymentDetails from "../contractDetails/brokerPaymentDetails";
import { DialogModal } from "../../../components/common/dialog";
import EscalationForm from "../EscalationForm";
import { InputType } from "../../../components/forms/textInput";
import TextArea from "../../../components/forms/textarea/TextArea";
import SellerPaymentDetails from "../contractDetails/paymentDetails/sellerPaymentDetails";
import BuyerPaymentDetails from "../contractDetails/paymentDetails/buyerPaymentDetails";
import BuyerFeeBreakdown from "../contractDetails/paymentDetails/buyerFeeBreakdown";

import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../enums";
import {
  EContractType,
  EFeePayee,
  ESenderRole,
  ETrasactionsType,
  IContractActionsResponseData,
  TransactionStatus,
  voucherContent,
} from "../../../setup/api/services/contract/models";
import { getUserId } from "../../../setup/auth/oidc";
import { ReceiverInfo } from "../contractDetails/receiverInfo";
import TransactionDetails from "../contractDetails/transactionDetails";
import { IContractProps } from "../models";
import { HiddenDivider } from "./hiddenDivider";

// styles
import styles from "./styles.module.scss";
import {
  BTN_HAVE_PROBLEM,
  MODAL_ESCALATION_TITLE,
  EXTENDED_SHIPPING_TIME,
  ENTER_THE_SHIPPING_PERIOD,
  ADDITIONAL_PERIOD,
  TO_BE_SURE,
  EXTENDED_INSPECTION_TIME,
  ENTER_INSPECTION_PERIOD,
  ACCEPT_PAYMENT,
  REJECT_PAYMENT,
  THE_BUYER,
  THE_SELLER,
  PAYMENT_DETAILS,
  TRANSFER_TO_SELLER,
  TRANSFER_TO_BUYER,
} from "./constants";
import {
  getContractActions,
  getContractVoucher,
} from "../../../setup/api/services/contract";
import { Tab, Tabs } from "@mui/material";

import {
  doAction,
  getAdminActorRole,
} from "../../../setup/api/services/contractActions";
import { EContractActions } from "../../../setup/api/services/contractActions/enum";
import { IContractActionRequestBody } from "../../../setup/api/services/contractActions/models";

import { ContractSubTitle } from "../titles";
import { findPartyForProfile } from "../../../setup/util/contract";
interface ILeftSection extends IContractProps {
  refreshContractDetails: any;
  /** Leave out the status card, for pages that show their own. */
  hideStatus?: boolean;
}
enum LOADING {
  NONE = "NONE",
  ACCEPT = "ACCEPT",
  REJECT = "REJECT",
}

enum TabType {
  SELLER = "SELLER",
  BUYER = "BUYER",
}

// context providers
import { ContractContext } from "../../../providers/contract";
import CashoutModal from "../../../components/contract/cashoutModal";
import { checkCashoutPayment } from "../../../setup/api/services/payment";
import StatusAlt from "../contractDetails/status/StatusAlt";
import {
  FAILED_CASH_OUT,
  FAILED_CASH_OUT_DESC,
  PENDING_CASHOUT,
  PENDING_CASHOUT_DESCRIPTION,
} from "../../../components/contractListItem/constants";
import { SHOW_REFERNCE_DETAILS } from "../contractDetails/transactionDetails/constants";

const LeftSection = ({
  contract,
  refreshContractDetails,
  hideStatus,
}: ILeftSection) => {
  const [tabType, setTabType] = React.useState<TabType>(TabType.SELLER);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [contractActions, setContractActions] =
    useState<IContractActionsResponseData[]>();
  const [isCashoutPending, setIsCashoutPending] = useState<boolean>(false);

  const { contractType } = useContext(ContractContext);

  const [adminTransactionIs, setAdminTransactionId] = useState<string>();
  const [counterPeriod, setCounterPeriod] = useState<Date>();
  const [showPeriodDeliveryModal, setShowPeriodDeliveryModal] =
    useState<boolean>(false);
  const [showPeriodInspectionModal, setShowPeriodInspectionModal] =
    useState<boolean>(false);
  const [showCashoutModal, setShowCashoutModal] = useState<boolean>(false);
  const [deliveryPeriod, setDeliveryPeriod] = useState<number>(
    contract?.deliveryInDays
  );
  const { t } = useTranslation();
  const [inspectionPeriod, setInspectionPeriod] = useState<number>(
    contract?.inspectionPeriod
  );
  const [loading, setLoading] = useState<LOADING>(LOADING.NONE);

  const [voucherData, setVoucherData] = useState<voucherContent>();

  const userId = getUserId();

  const getActorType = useCallback(() => {
    if (contract.senderId == userId) return ESenderRole.PROVIDER;
    else return ESenderRole.CUSTOMER;
  }, [contract.senderId, userId]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isTabbyPayment = useMemo(() => {
    return contract?.transactions?.some(
      (transaction) =>
        transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
        transaction?.paymentMethod === "TABBY"
    );
  }, [contract]);

  const isNeoPayment = useMemo(() => {
    return contract?.transactions?.some(
      (transaction) =>
        transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
        transaction?.paymentMethod === "NEO"
    );
  }, [contract]);

  const isTamaraPayment = useMemo(() => {
    return contract?.transactions?.some(
      (transaction) =>
        transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
        transaction?.paymentMethod === "TAMARA"
    );
  }, [contract]);

  const isTabbyTransactionDone = useMemo(() => {
    return (
      (isTabbyPayment || isNeoPayment || isTamaraPayment) &&
      (contract?.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_CLOSE ||
        contract?.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_RESOLVE) &&
      contract?.transactions?.some(
        (transaction) =>
          transaction?.type === ETrasactionsType.RELEASE_TO_PROVIDER ||
          transaction?.type === ETrasactionsType.CLOSE_RELEASE_TO_PROVIDER ||
          (transaction?.type === ETrasactionsType.CASH_OUT &&
            transaction?.status !== "FAILED")
      )
    );
  }, [contract, isTabbyPayment, isNeoPayment, isTamaraPayment]);

  const getVoucherDetails = useCallback(async () => {
    try {
      const data = await getContractVoucher(
        contract?.shortId,
        String(contract?.customer?.id)
      );
      const contentData = data?.data?.content[0];
      if (contentData) {
        setVoucherData(contentData);
      }
    } catch (error) {
      console.log("error value", error);
    }
  }, [contract?.customer?.id, contract?.shortId]);

  useEffect(() => {
    if (
      contract?.status !== ECONTRACT_STATUS.CREATED &&
      contract?.status !== ECONTRACT_STATUS.SUBMITTED &&
      contract?.hasVoucher &&
      contract?.customer?.id &&
      !contract?.organizationCode &&
      contract?.shortId
    ) {
      getVoucherDetails();
    }
  }, [
    contract?.customer?.id,
    contract?.organizationCode,
    contract?.status,
    contract?.shortId,
    getVoucherDetails,
    contract?.hasVoucher,
  ]);

  const handleMobileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabType) => {
    setTabType(newValue);
  };

  const getAdminReferenceId = useCallback(() => {
    const adminTrasactions = contract.transactions?.filter((transaction) => {
      if (
        transaction.type !== ETrasactionsType.CUSTOMER_PAY &&
        transaction.type !== ETrasactionsType.MANUAL_BANK_TRANSFER
      ) {
        return transaction;
      }
    });
    if (adminTrasactions && adminTrasactions?.length > 0) {
      setAdminTransactionId(adminTrasactions?.pop()?.id);
    }
  }, [contract.transactions]);

  const fetchContractActions = useCallback(
    async (id: string) => {
      const result = await getContractActions(id);
      setContractActions(result?.data);
      let deliveryStartDate: Date | undefined = undefined;
      let inspectionStartDate: Date | undefined = undefined;
      if (result?.data && result?.data?.length) {
        result?.data?.map((action) => {
          if (action?.to === "WAITING_FOR_DELIVERY") {
            deliveryStartDate = action?.createdAt;
          }
          if (action?.to === "ITEM_INSPECTION") {
            inspectionStartDate = action?.createdAt;
            deliveryStartDate = undefined;
          }
        });
      }
      if (inspectionStartDate || deliveryStartDate) {
        const timeRemaining = new Date(
          String(inspectionStartDate || deliveryStartDate)
        );
        inspectionStartDate
          ? timeRemaining.setDate(
              timeRemaining.getDate() + contract?.inspectionPeriod
            )
          : timeRemaining.setDate(
              timeRemaining.getDate() + contract?.deliveryInDays
            );
        setCounterPeriod(timeRemaining);
      }
    },
    [contract?.deliveryInDays, contract?.inspectionPeriod]
  );

  useEffect(() => {
    fetchContractActions(contract?.id);
  }, [contract, fetchContractActions, contract?.status]);

  useEffect(() => {
    getAdminReferenceId();
  }, [contract, getAdminReferenceId]);

  const openPeriodModal = useCallback(() => {
    handleMobileMenuClose();
    contract?.status === "WAITING_FOR_DELIVERY" &&
      setShowPeriodDeliveryModal(true);
    contract?.status === "ITEM_INSPECTION" &&
      setShowPeriodInspectionModal(true);
  }, [contract?.status]);

  const updateContractPeriods = useCallback(async () => {
    if (!userId) return;
    const body: IContractActionRequestBody = {
      contractId: contract.id,
      userId: userId,
      actorRole: getAdminActorRole(),
      receiverId: contract?.receiverId || userId,
      senderId: contract.senderId,
      contractAction: EContractActions.ADMIN_CHANGE_DELIVERY_PERIOD,
      extendedDeliveryPeriod: deliveryPeriod,
    };
    await doAction(body);
    setDeliveryPeriod(0);
    setShowPeriodDeliveryModal(false);
    window.location.reload();
  }, [
    contract.id,
    contract?.receiverId,
    contract.senderId,
    deliveryPeriod,
    userId,
  ]);

  const renderDeliveryModal = useCallback(() => {
    return (
      <DialogModal
        title={EXTENDED_SHIPPING_TIME}
        open={showPeriodDeliveryModal}
        handleClose={() => setShowPeriodDeliveryModal(false)}>
        <div className={styles.period_modal}>
          <p className={classNames(styles.subtitle, styles["rightAlign"])}>
            {ENTER_THE_SHIPPING_PERIOD}
          </p>
          <TextArea
            handleChange={(event) =>
              setDeliveryPeriod(Number(event?.target?.value))
            }
            label={ADDITIONAL_PERIOD}
            type={InputType.NUMBER}
            defaultValue={String(deliveryPeriod)}
            rangeSelector={[1, 100]}
          />
          <div className={styles["mt2"]}>
            <PrimaryButton onClick={updateContractPeriods}>
              {TO_BE_SURE}
            </PrimaryButton>
          </div>
        </div>
      </DialogModal>
    );
  }, [deliveryPeriod, showPeriodDeliveryModal, updateContractPeriods]);

  const updateInspectionPeriod = useCallback(async () => {
    if (!userId) return;
    const body: IContractActionRequestBody = {
      contractId: contract.id,
      userId: userId,
      actorRole: getAdminActorRole(),
      receiverId: contract?.receiverId || userId,
      senderId: contract.senderId,
      contractAction: EContractActions.ADMIN_CHANGE_INSPECTION_PERIOD,
      extendedInspectionPeriod: inspectionPeriod,
    };
    await doAction(body);
    setInspectionPeriod(0);
    setShowPeriodInspectionModal(false);
    window.location.reload();
  }, [
    contract.id,
    contract?.receiverId,
    contract.senderId,
    inspectionPeriod,
    userId,
  ]);

  // A milestone has no receiverId of its own — its customers live in
  // partiesProfiles — so the payment being verified is addressed by the
  // profile id of the customer whose payment awaits approval, the id the
  // milestone payee actions use. Every other contract keeps its receiverId.
  const paymentReceiverId = useMemo(() => {
    if (
      contract?.type !== EContractType.MILESTONE_CONTRACT ||
      contract?.receiverId
    )
      return contract?.receiverId;
    const customers = contract?.partiesProfiles?.CUSTOMER || [];
    const awaitingApproval = customers.filter(
      (profile) =>
        findPartyForProfile(contract?.parties, profile, ESenderRole.CUSTOMER)
          ?.status === EPARTY_STATUS.PAYMENT_PENDING_APPROVAL
    );
    const customer =
      awaitingApproval.length === 1
        ? awaitingApproval[0]
        : customers.length === 1
        ? customers[0]
        : undefined;
    return customer?.id !== undefined
      ? String(customer.id)
      : contract?.receiverId;
  }, [contract]);

  const acceptPayment = useCallback(async () => {
    if (!userId) return;
    setLoading(LOADING.ACCEPT);
    try {
      const body = {
        contractId: contract?.id,
        userId: userId,
        actorRole: getAdminActorRole(),
        receiverId: String(paymentReceiverId),
        senderId: String(contract?.senderId),
        contractAction: EContractActions.ADMIN_VERIFY_PAYMENT,
      };
      await doAction(body);
      setLoading(LOADING.NONE);
      window.location.reload();
    } catch (error) {
      setLoading(LOADING.NONE);
      console.log("error", error);
    }
  }, [contract?.id, paymentReceiverId, contract?.senderId, userId]);

  const rejectPayment = useCallback(async () => {
    if (!userId) return;
    setLoading(LOADING.REJECT);
    try {
      const body = {
        contractId: contract?.id,
        userId: userId,
        actorRole: getAdminActorRole(),
        receiverId: String(paymentReceiverId),
        senderId: String(contract?.senderId),
        contractAction: EContractActions.ADMIN_DENY_PAYMENT,
      };
      await doAction(body);
      setLoading(LOADING.NONE);
      window.location.reload();
    } catch (error) {
      setLoading(LOADING.NONE);
      console.log("error", error);
    }
  }, [contract?.id, paymentReceiverId, contract?.senderId, userId]);

  const renderInspectionModal = useCallback(() => {
    return (
      <DialogModal
        title={EXTENDED_INSPECTION_TIME}
        open={showPeriodInspectionModal}
        handleClose={() => setShowPeriodDeliveryModal(false)}>
        <div className={styles.period_modal}>
          <p className={classNames(styles.subtitle, styles["rightAlign"])}>
            {ENTER_INSPECTION_PERIOD}
          </p>
          <TextArea
            handleChange={(event) =>
              setInspectionPeriod(Number(event?.target?.value))
            }
            label={ADDITIONAL_PERIOD}
            type={InputType.NUMBER}
            defaultValue={String(inspectionPeriod)}
            rangeSelector={[1, 100]}
          />
          <div className={styles["mt2"]}>
            <PrimaryButton onClick={updateInspectionPeriod}>
              {TO_BE_SURE}
            </PrimaryButton>
          </div>
        </div>
      </DialogModal>
    );
  }, [inspectionPeriod, showPeriodInspectionModal, updateInspectionPeriod]);

  const escalationType: EContractActions | "" = useMemo(() => {
    if (contract?.escalations) {
      return contract?.escalations[contract?.escalations?.length - 1]
        ?.escalationType as unknown as EContractActions;
    } else {
      return "";
    }
  }, [contract?.escalations]);

  const makeCashoutCheck = useCallback(async () => {
    let beneficiaryId;
    const currentPendingCashoutTransaction = contract?.transactions?.find(
      (transaction) =>
        transaction.type === ETrasactionsType.CASH_OUT &&
        transaction.status === TransactionStatus.PENDING
    );

    if (contract.type === EContractType.BROKERAGE_CONTRACT) {
      beneficiaryId =
        contract?.partiesProfiles?.PROVIDER?.[0]?.userIdentification;
    } else {
      beneficiaryId = currentPendingCashoutTransaction?.beneficiaryId;
    }

    try {
      const statusData = await checkCashoutPayment(
        currentPendingCashoutTransaction?.referenceTransactionId ||
          contract?.transactions?.[0]?.referenceTransactionId,
        beneficiaryId || contract?.provider?.id || contract?.customer?.id || ""
      );
      const status = statusData?.data?.status;
      if (
        statusData?.data?.transactionType === "CASHOUT" &&
        (status === "SUCCESS" || status === "INITIATED" || status === "PENDING")
      ) {
        setIsCashoutPending(true);
        return;
      }
      setIsCashoutPending(false);
      return;
    } catch (err) {
      setIsCashoutPending(false);
      console.error(err);
    }
  }, []);

  const checkCashoutRunning = useCallback(async () => {
    if (
      contract?.transactions?.length &&
      contract?.transactions?.some(
        (transaction) =>
          transaction?.type === "CASH_OUT" && transaction?.status === "PENDING"
      )
    ) {
      try {
        await makeCashoutCheck();
      } catch (error) {
        console.error();
      }
    }
  }, [contract?.transactions, makeCashoutCheck]);

  const handleCloseModal = useCallback(() => {
    setShowCashoutModal(false);
  }, []);

  useEffect(() => {
    checkCashoutRunning();
  }, [checkCashoutRunning]);

  const contractCashoutState = useMemo(() => {
    const cashOutRequestIndex = contract?.transactions
      ?.map((transaction) => transaction?.type)
      ?.lastIndexOf(ETrasactionsType.CASH_OUT);

    return contract?.transactions?.[cashOutRequestIndex]?.status;
  }, [contract]);

  return (
    <div className={styles.left_container}>
      <Modal
        open={showContractModal}
        onClose={() => setShowContractModal(!showContractModal)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <div className={styles.modal}>
          <h1 className={classNames(styles.link)}>
            {`${adminTransactionIs
              ?.charAt(0)
              ?.toUpperCase()}${adminTransactionIs?.slice(1)}`}
          </h1>
          <p
            className={
              styles.transactionDetails
            }>{`${"  "}  :  ${SHOW_REFERNCE_DETAILS}`}</p>
        </div>
      </Modal>
      {renderDeliveryModal()}
      {renderInspectionModal()}
      <CashoutModal
        showModal={showCashoutModal}
        closeModal={handleCloseModal}
        userType={tabType === TabType.BUYER ? "buyer" : "seller"}
        checkCashoutStatus={makeCashoutCheck}
        refreshContractDetails={refreshContractDetails}
      />
      {!hideStatus && (
        <div className={styles["mb5"]} style={{ width: "100%" }}>
          {contractType === EContractType.BROKERAGE_CONTRACT ? (
            <StatusTimeline />
          ) : (
            <Status
              contract={contract}
              status={contract?.status}
              escalationType={escalationType}
              actorType={getActorType()}
            />
          )}
        </div>
      )}
      <div className={styles["mb2"]}>
        <HiddenDivider smUp />
        {adminTransactionIs && (
          <TransactionDetails
            handleModal={() => setShowContractModal(!showContractModal)}
          />
        )}
        {(((contract?.senderId ||
          contract?.partiesProfiles?.PROVIDER?.[0]?.id) &&
          contract.senderRole !== ESenderRole.CUSTOMER) ||
          ((contract?.receiverId ||
            contract?.partiesProfiles?.PROVIDER?.[0]?.id) &&
            contract.senderRole === ESenderRole.CUSTOMER)) && (
          <ReceiverInfo
            receiverType="sender"
            contractStatus={contract.status}
            renderUserId={
              (contract?.senderRole === ESenderRole.CUSTOMER
                ? contract?.receiverId
                : contract.senderId) ||
              contract?.partiesProfiles?.PROVIDER?.[0]?.id
            }
          />
        )}
      </div>
      {(((contract?.receiverId ||
        contract?.partiesProfiles?.CUSTOMER?.[0]?.id) &&
        contract.senderRole !== ESenderRole.CUSTOMER) ||
        ((contract?.senderId || contract?.partiesProfiles?.CUSTOMER?.[0]?.id) &&
          contract.senderRole === ESenderRole.CUSTOMER)) && (
        <div className={styles["mb2"]}>
          <div>
            <HiddenDivider smUp />
            <ReceiverInfo
              receiverType="receiver"
              contractStatus={contract.status}
              renderUserId={
                (contract?.senderRole === ESenderRole.CUSTOMER
                  ? contract?.senderId
                  : contract.receiverId) ||
                contract?.partiesProfiles?.CUSTOMER?.[0]?.id
              }
            />
          </div>
        </div>
      )}
      {isTabbyPayment && (
        <div className={styles["tabby_container"]}>
          <img
            src="/assets/images/svg/tabby.svg"
            alt="tabby"
            width="66px"
            height="24px"
          />

          <p className="p2">{t("lbl_paid_with_tabby")}</p>
        </div>
      )}
      {isNeoPayment && (
        <div className={styles["neo_container"]}>
          <img
            src="/assets/images/svg/NEO.svg"
            alt="neo"
            width="66px"
            height="24px"
          />

          <p className="p2">{t("lbl_paid_with_neo")}</p>
        </div>
      )}
      {isTamaraPayment && (
        <div className={styles["tamara_container"]}>
          <img
            src="/assets/images/svg/Tamara.svg"
            alt="tamara"
            width="66px"
            height="24px"
          />

          <p className="p2">{t("lbl_paid_with_tamara")}</p>
        </div>
      )}
      <HiddenDivider smUp />
      <ContractSubTitle text={PAYMENT_DETAILS} />
      {contractType === EContractType.BROKERAGE_CONTRACT &&
      contract.status !== ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER ? (
        <BrokerPaymentDetails />
      ) : (
        <>
          {contract?.status === ECONTRACT_STATUS.CREATED &&
          contract.feePayee !== EFeePayee.CUSTOMER ? (
            <SellerPaymentDetails contract={contract} />
          ) : (
            <>
              <div className={styles.tab_container}>
                <Tabs
                  style={{ alignItems: "center" }}
                  className={styles.tab_wrapper}
                  value={tabType}
                  onChange={handleTabChange}>
                  <Tab
                    value={TabType.BUYER}
                    label={THE_BUYER}
                    sx={{
                      paddingLeft: "1rem",
                      paddingRight: "1rem",
                      fontSize: { xs: "0.75em", md: "1em" },
                    }}
                  />
                  <Tab
                    value={TabType.SELLER}
                    label={THE_SELLER}
                    sx={{
                      paddingLeft: "1rem",
                      paddingRight: "1rem",
                      fontSize: { xs: "0.75em", md: "1em" },
                    }}
                  />
                </Tabs>
              </div>

              <>
                {tabType === TabType.BUYER &&
                  (contract.feePayee === EFeePayee.CUSTOMER ? (
                    <BuyerFeeBreakdown contract={contract} />
                  ) : (
                    <BuyerPaymentDetails
                      contract={contract}
                      voucherData={voucherData}
                      contractActions={contractActions}
                    />
                  ))}
                {tabType === TabType.SELLER && (
                  <SellerPaymentDetails contract={contract} />
                )}
              </>
            </>
          )}
        </>
      )}
      {contract.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY ||
        (contract.status === ECONTRACT_STATUS.ITEM_INSPECTION && (
          <PrimaryButton onClick={() => setShowEscalationModal(true)}>
            {BTN_HAVE_PROBLEM}
          </PrimaryButton>
        ))}
      {(contract.status === ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION ||
        (contract.status === ECONTRACT_STATUS.PARTIALLY_PAID &&
          contract.parties?.filter((p) => p.role === ESenderRole.CUSTOMER)
            .length === 1)) && (
        <div
          className={classNames(
            styles.bottom_section,
            styles.buttons_container
          )}>
          <PrimaryButton backgroundColor={"#44C16E"} onClick={acceptPayment}>
            {ACCEPT_PAYMENT}
          </PrimaryButton>
          <PrimaryButton
            buttonType={PrimaryButtonType.DANGER}
            onClick={rejectPayment}>
            {REJECT_PAYMENT}
          </PrimaryButton>
        </div>
      )}
      {contract.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
        tabType === TabType.BUYER &&
        !isCashoutPending && (
          <PrimaryButton onClick={() => setShowCashoutModal(true)}>
            {TRANSFER_TO_BUYER}
          </PrimaryButton>
        )}
      {(contract.status === ECONTRACT_STATUS.ITEM_ACCEPTED ||
        contract.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_RESOLVE ||
        contract.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_CLOSE ||
        contract.status === ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER) && (
        <>
          {contractCashoutState === "PENDING" && (
            <div className={styles["alternate_status"]}>
              <StatusAlt
                img={"/assets/images/svg/pendingBank.svg"}
                title={PENDING_CASHOUT}
                description={PENDING_CASHOUT_DESCRIPTION}
                backGroundColor="#FFC35400"
              />
            </div>
          )}
          {contractCashoutState === "FAILED" && (
            <div className={styles["alternate_status"]}>
              <StatusAlt
                img={"/assets/images/svg/RejectedIcon.svg"}
                title={FAILED_CASH_OUT}
                description={FAILED_CASH_OUT_DESC}
                backGroundColor="#FFC35400"
              />
            </div>
          )}
        </>
      )}
      <DialogModal
        title={MODAL_ESCALATION_TITLE}
        open={showEscalationModal}
        handleClose={async () => {
          setShowEscalationModal(false);
        }}>
        <EscalationForm
          isWaitingDelivery={
            contract.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY
          }
          isWaitingInspection={
            contract.status === ECONTRACT_STATUS.ITEM_INSPECTION
          }
          sellerId={
            contract.type === EContractType.MILESTONE_CONTRACT
              ? contract.senderId ||
                `${contract?.partiesProfiles?.PROVIDER?.[0]?.id ?? ""}`
              : contract.senderId
          }
          buyerId={
            // a milestone carries its parties in partiesProfiles only
            contract.type === EContractType.MILESTONE_CONTRACT
              ? contract.receiverId ||
                `${contract?.partiesProfiles?.CUSTOMER?.[0]?.id ?? ""}`
              : contract.receiverId
          }
        />
      </DialogModal>
    </div>
  );
};

export default LeftSection;
